/**
 * src/mesh/broadcast/relay-policy-manager.ts
 *
 * Hybrid WebRTC Dynamic Relay Policy Manager (Step 3.1).
 * Seamlessly transitions between Full P2P Mesh (<=8 peers) and Mediasoup SFU (>8 peers).
 * Enforces bandwidth guards to cap publisher egress to a single upstream uplink.
 */

import { EventEmitter } from '../../core/event-emitter.js';

export type RelayMode = 'P2P_MESH' | 'MEDIASOUP_SFU';

export interface RelayTopologyState {
  channelId: string;
  subscriberCount: number;
  currentMode: RelayMode;
  previousMode?: RelayMode;
  renegotiationCount: number;
  lastHandoffTimestamp: number;
  bandwidthGuardActive: boolean;
  uplinkEgressMultiplier: number; // 1x in SFU mode, N in P2P mode
  projectedUploadBitrateKbps: number;
}

export interface RelayHandoffEvent {
  channelId: string;
  fromMode: RelayMode;
  toMode: RelayMode;
  subscriberCount: number;
  timestamp: number;
  sdpRenegotiationReason: string;
}

export class RelayPolicyManager extends EventEmitter {
  public static readonly P2P_THRESHOLD_PEERS = 8;
  public static readonly BASE_STREAM_BITRATE_KBPS = 2500; // 2.5 Mbps 1080p

  private static instance: RelayPolicyManager;
  private channelTopologies: Map<string, RelayTopologyState> = new Map();

  private constructor() {
    super();
  }

  public static getInstance(): RelayPolicyManager {
    if (!RelayPolicyManager.instance) {
      RelayPolicyManager.instance = new RelayPolicyManager();
    }
    return RelayPolicyManager.instance;
  }

  /**
   * Evaluates channel subscriber count and transitions topology between P2P Mesh and Mediasoup SFU.
   */
  public updateSubscriberCount(
    channelId: string,
    subscriberCount: number
  ): {
    mode: RelayMode;
    renegotiationRequired: boolean;
    state: RelayTopologyState;
  } {
    const existing = this.channelTopologies.get(channelId);
    const targetMode: RelayMode =
      subscriberCount > RelayPolicyManager.P2P_THRESHOLD_PEERS ? 'MEDIASOUP_SFU' : 'P2P_MESH';

    const currentMode = existing?.currentMode ?? 'P2P_MESH';
    const renegotiationRequired = existing ? currentMode !== targetMode : false;

    const uplinkEgressMultiplier = targetMode === 'MEDIASOUP_SFU' ? 1 : Math.max(1, subscriberCount);
    const projectedUploadBitrateKbps =
      uplinkEgressMultiplier * RelayPolicyManager.BASE_STREAM_BITRATE_KBPS;

    const state: RelayTopologyState = {
      channelId,
      subscriberCount,
      currentMode: targetMode,
      previousMode: existing?.currentMode,
      renegotiationCount: (existing?.renegotiationCount ?? 0) + (renegotiationRequired ? 1 : 0),
      lastHandoffTimestamp: renegotiationRequired ? Date.now() : (existing?.lastHandoffTimestamp ?? Date.now()),
      bandwidthGuardActive: targetMode === 'MEDIASOUP_SFU',
      uplinkEgressMultiplier,
      projectedUploadBitrateKbps,
    };

    this.channelTopologies.set(channelId, state);

    if (renegotiationRequired) {
      const handoffEvent: RelayHandoffEvent = {
        channelId,
        fromMode: currentMode,
        toMode: targetMode,
        subscriberCount,
        timestamp: Date.now(),
        sdpRenegotiationReason:
          targetMode === 'MEDIASOUP_SFU'
            ? `Subscribers exceeded P2P threshold (> ${RelayPolicyManager.P2P_THRESHOLD_PEERS}). Handing off to Mediasoup SFU.`
            : `Subscribers dropped below threshold (<= ${RelayPolicyManager.P2P_THRESHOLD_PEERS}). Reverting to zero-cost P2P mesh.`,
      };

      this.emit('topology:handoff', handoffEvent);
    }

    return {
      mode: targetMode,
      renegotiationRequired,
      state,
    };
  }

  public getTopologyState(channelId: string): RelayTopologyState | undefined {
    return this.channelTopologies.get(channelId);
  }

  /**
   * Calculates bandwidth saved by the Mediasoup SFU handoff.
   */
  public calculateBandwidthSavings(channelId: string): {
    p2pBitrateKbps: number;
    sfuBitrateKbps: number;
    savedBitrateKbps: number;
    savingsPercent: number;
  } {
    const state = this.channelTopologies.get(channelId);
    if (!state) {
      return { p2pBitrateKbps: 0, sfuBitrateKbps: 0, savedBitrateKbps: 0, savingsPercent: 0 };
    }

    const n = Math.max(1, state.subscriberCount);
    const p2pBitrateKbps = n * RelayPolicyManager.BASE_STREAM_BITRATE_KBPS;
    const sfuBitrateKbps = RelayPolicyManager.BASE_STREAM_BITRATE_KBPS; // 1x upstream to SFU

    if (state.currentMode === 'MEDIASOUP_SFU') {
      const savedBitrateKbps = Math.max(0, p2pBitrateKbps - sfuBitrateKbps);
      const savingsPercent = Math.round((savedBitrateKbps / p2pBitrateKbps) * 100);
      return { p2pBitrateKbps, sfuBitrateKbps, savedBitrateKbps, savingsPercent };
    }

    return { p2pBitrateKbps, sfuBitrateKbps, savedBitrateKbps: 0, savingsPercent: 0 };
  }

  public clear(): void {
    this.channelTopologies.clear();
  }
}

export const relayPolicyManager = RelayPolicyManager.getInstance();
